import { PlayerInput } from '@shared/types';
import { TouchController } from './TouchController';

/** Threshold for joystick axis → boolean conversion */
const JOY_THRESHOLD = 0.3;

export class InputManager {
  private keys = new Set<string>();
  private itemPressed = false; // edge-detect for item use (keyboard)
  private touchItemWas = false; // edge-detect for item use (touch)
  private onKeyDown: (e: KeyboardEvent) => void;
  private onKeyUp: (e: KeyboardEvent) => void;
  private onBlur: () => void;

  readonly touch: TouchController;

  constructor() {
    this.onKeyDown = (e: KeyboardEvent) => {
      // Prevent default for game keys to avoid browser scroll/tab behavior
      if (
        e.code === 'ArrowUp' ||
        e.code === 'ArrowDown' ||
        e.code === 'ArrowLeft' ||
        e.code === 'ArrowRight' ||
        e.code === 'Space' ||
        e.code === 'Escape' ||
        e.code === 'Tab'
      ) {
        e.preventDefault();
      }
      this.keys.add(e.code);
    };

    this.onKeyUp = (e: KeyboardEvent) => {
      this.keys.delete(e.code);
    };

    this.onBlur = () => {
      this.keys.clear();
      this.itemPressed = false;
    };

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);

    // Create touch controls (auto-shows on touch devices)
    this.touch = new TouchController({
      buttons: [
        { id: 'item', label: 'ITEM', color: '#44bbff' },
        { id: 'drift', label: 'DRIFT', color: '#ffaa44' },
        { id: 'trick', label: 'TRICK', color: '#44ff88' },
      ],
      joystickSide: 'left',
      opacity: 0.55,
    });
  }

  /** Get current input state. Call once per frame. */
  getInput(): PlayerInput {
    // Note: In our physics convention, input.left increases yaw (turns toward +Z)
    // which is screen-right from the camera's perspective. We swap the key mappings
    // so A/ArrowLeft → input.right (screen-left) and D/ArrowRight → input.left (screen-right).
    const kbForward = this.keys.has('ArrowUp') || this.keys.has('KeyW');
    const kbBackward = this.keys.has('ArrowDown') || this.keys.has('KeyS');
    const kbLeft = this.keys.has('ArrowRight') || this.keys.has('KeyD');
    const kbRight = this.keys.has('ArrowLeft') || this.keys.has('KeyA');
    const kbDrift = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    const kbTrick = this.keys.has('KeyE');

    // Touch input
    const ts = this.touch.getState();
    // Joystick: x positive = right on screen, y negative = up/forward
    const touchForward = ts.joystick.y < -JOY_THRESHOLD;
    const touchBackward = ts.joystick.y > JOY_THRESHOLD;
    // Swap left/right same as keyboard to match physics convention
    const touchLeft = ts.joystick.x > JOY_THRESHOLD;
    const touchRight = ts.joystick.x < -JOY_THRESHOLD;
    const touchDrift = ts.buttons.drift ?? false;
    const touchItem = ts.buttons.item ?? false;
    const touchTrick = ts.buttons.trick ?? false;

    const input: PlayerInput = {
      forward: kbForward || touchForward,
      backward: kbBackward || touchBackward,
      left: kbLeft || touchLeft,
      right: kbRight || touchRight,
      drift: kbDrift || touchDrift,
      item: false,
      trick: kbTrick || touchTrick,
    };

    // Edge detect for item use — keyboard (Space key)
    const spaceDown = this.keys.has('Space');
    if (spaceDown && !this.itemPressed) {
      input.item = true;
    }
    this.itemPressed = spaceDown;

    // Edge detect for item use — touch button
    if (touchItem && !this.touchItemWas) {
      input.item = true;
    }
    this.touchItemWas = touchItem;

    return input;
  }

  /** Remove event listeners */
  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.keys.clear();
    this.itemPressed = false;
    this.touch.dispose();
  }
}
